import type { Video } from "@unbox/api";
import { Anchor, ChevronDown, Colors, Sale03, Tag01 } from "@untitledui/icons";
import { type ComponentType, type ReactNode, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/base/badges/badges";
import { toTitleCase } from "@/utils/toTitleCase";

// Video Insights Component
export const VideoInsights = ({ data }: { data: Video }) => {
  return (
    <div className="self-stretch inline-flex justify-start items-start gap-12 flex-wrap">
      <div className="flex-1 max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4 min-w-[240px]">
        <div className="inline-flex justify-start items-center gap-1.5">
          <Colors className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">Video Style</div>
        </div>
        <div className="self-stretch inline-flex justify-start items-start gap-2 flex-wrap content-start">
          {data.archetypes?.map((style) => (
            <Badge key={style} className="bg-secondary text-gray-blue-700 border border-gray-blue-200">
              {style}
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex-1 max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4 min-w-[240px]">
        <div className="inline-flex justify-start items-center gap-1.5">
          <Anchor className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">Hook</div>
        </div>
        <div className="self-stretch inline-flex justify-start items-start gap-2 flex-wrap content-start">
          {data.hooks?.map((hook) => (
            <Badge key={hook} className="bg-secondary text-gray-blue-700 border border-gray-blue-200">
              {hook}
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex-1 max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4 min-w-[240px]">
        <div className="inline-flex justify-start items-center gap-1.5">
          <Tag01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">Selling Points</div>
        </div>
        <div className="self-stretch inline-flex justify-start items-start gap-2 flex-wrap content-start">
          {data.sellingPoints?.map((point) => (
            <Badge key={point} className="bg-secondary text-gray-blue-700 border border-gray-blue-200">
              {point}
            </Badge>
          ))}
        </div>
      </div>
      <div className="flex-1 max-w-[768px] rounded-lg inline-flex flex-col justify-start items-start gap-4 min-w-[240px]">
        <div className="inline-flex justify-start items-center gap-1.5">
          <Sale03 className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">Sales Tactics</div>
        </div>
        <div className="self-stretch inline-flex justify-start items-start gap-2 flex-wrap content-start">
          {data.tactics?.map((tactic) => (
            <Badge key={tactic} className="bg-secondary text-gray-blue-700 border border-gray-blue-200">
              {toTitleCase(tactic.replaceAll("_", " "))}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

// Collapsible text accordion with auto-expand/collapse based on visual overflow
interface CollapsibleTextProps {
  title: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  content: ReactNode;
  emptyText?: string;
  contentClassName?: string;
}

export const CollapsibleText = ({
  title,
  icon: Icon,
  content,
  emptyText = "No content available",
  contentClassName = "text-primary font-medium",
}: CollapsibleTextProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Detect if content overflows 2 lines (line-clamp-2)
  useEffect(() => {
    if (contentRef.current && content) {
      // Check if scrollHeight exceeds clientHeight (means content is clamped)
      const isOverflowing = contentRef.current.scrollHeight > contentRef.current.clientHeight;
      setHasOverflow(isOverflowing);
    } else {
      setHasOverflow(false);
    }
  }, [content]);

  // If no overflow, just show static content
  if (!hasOverflow) {
    return (
      <div className="w-full flex flex-col justify-start items-start gap-3">
        <div className="flex justify-start items-center gap-1.5">
          <Icon className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">{title}</div>
        </div>
        <div className="w-full">
          <div
            ref={contentRef}
            className={`self-stretch text-left text-sm leading-tight line-clamp-2 p-4 bg-secondary rounded-md ${contentClassName}`}
          >
            {content || <span className="italic text-quaternary">{emptyText}</span>}
          </div>
        </div>
      </div>
    );
  }

  // Has overflow - show collapsible accordion
  return (
    <div className="w-full flex flex-col justify-start items-start gap-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full self-stretch inline-flex justify-between items-center cursor-pointer hover:opacity-80"
      >
        <div className="flex justify-start items-center gap-1.5">
          <Icon className="w-5 h-5 text-tertiary" strokeWidth={2} />
          <div className="justify-start text-tertiary text-sm font-medium leading-tight">{title}</div>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-tertiary transition-transform ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {!isOpen && (
        <button type="button" onClick={() => setIsOpen(true)} className="relative w-full cursor-pointer">
          <div
            ref={contentRef}
            className={`self-stretch text-left text-sm leading-tight line-clamp-2 p-4 bg-secondary rounded-md ${contentClassName}`}
          >
            {content || <span className="italic text-quaternary">{emptyText}</span>}
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[3.75rem] bg-gradient-to-t from-secondary via-secondary/50 to-transparent pointer-events-none rounded-b-md" />
        </button>
      )}
      {isOpen && (
        <div className="w-full">
          <div
            className={`self-stretch text-left text-sm leading-tight p-4 bg-secondary rounded-md ${contentClassName}`}
          >
            {content || <span className="italic text-quaternary">{emptyText}</span>}
          </div>
        </div>
      )}
    </div>
  );
};
